import {Box} from "@mui/material";
import {SxProps, Theme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {useTranslateForLanguage} from "crqlar-react-common/dist/utils/text.util";
import React, {useMemo} from "react";
import SigningFrame from "./SigningFrame";
import SigningLayout, {consentTextOf} from "./SigningLayout";
import {BOX_LABEL, SignatureBaseline, SignatureBox, SignatureFoot, SignaturePlaceholder} from "./SigningSignatureGuides";
import {signingFormat} from "./format.util";
import {DigitalSigningSnapshot, TipMode} from "./digitalSigning.types";
import {initialTipChoice, resolveTip, TipChoice} from "./tip.util";
import TipSelector, {tipOffered} from "./TipSelector";

export interface SignedTip {
    mode?: TipMode;
    presetIndex?: number;
    amountCents?: number;
}

export interface SignedSignature {
    imageUrl?: string;
    signedAt?: Date | string;
    language?: string;
}

interface Props {
    snapshot: DigitalSigningSnapshot;
    tip?: SignedTip;
    signature?: SignedSignature;
    sx?: SxProps<Theme>;
}

const choiceOf = (tip: SignedTip | undefined): TipChoice => {
    if (tip?.mode === 'PRESET' && typeof tip.presetIndex === 'number') return {mode: 'PRESET', presetIndex: tip.presetIndex};
    if (tip?.mode === 'CUSTOM') return {mode: 'CUSTOM'};
    return {mode: 'NONE'};
};

const isoOf = (value: Date | string | undefined) => (value instanceof Date ? value.toISOString() : value);

function DigitalSigningSignedView({snapshot, tip, signature, sx}: Props) {
    const {tFor} = useTranslateForLanguage();
    const language = signature?.language || snapshot.language || 'en';
    const t = tFor(language);
    const format = useMemo(
        () => signingFormat(language, snapshot.currency, snapshot.timeZone),
        [language, snapshot]
    );
    const offered = tipOffered(snapshot.tip) ? snapshot.tip : undefined;
    const choice = signature ? choiceOf(tip) : initialTipChoice(offered);
    const tipCents = signature
        ? (tip?.mode && tip.mode !== 'NONE' ? tip.amountCents ?? 0 : 0)
        : resolveTip(offered, choice, '')?.amountCents ?? 0;
    const signedAt = isoOf(signature?.signedAt);
    const signedAtText = [format.day(signedAt), format.time(signedAt)].filter(Boolean).join(' · ') || undefined;

    return (
        <SigningFrame theme={snapshot.theme} sx={sx}>
            <SigningLayout
                snapshot={snapshot}
                format={format}
                t={t}
                language={language}
                grandTotalCents={snapshot.totals.grossCents + tipCents}
                tip={!!offered && (
                    <TipSelector
                        tip={offered}
                        choice={choice}
                        customInput={''}
                        format={format}
                        language={language}
                        t={t}
                        readOnly={{customCents: tip?.mode === 'CUSTOM' ? tipCents : undefined}}
                    />
                )}
                signature={(
                    <SignatureBox touchLock={false}>
                        {!!signature?.imageUrl && (
                            <Box
                                component={'img'}
                                src={signature.imageUrl}
                                alt={t('Guest signature')}
                                sx={{position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain'}}
                            />
                        )}
                        <SignatureBaseline/>
                        {!signature?.imageUrl && <SignaturePlaceholder text={t('Sign here')} hidden={false}/>}
                        <SignatureFoot
                            label={t('Your signature')}
                            action={!!signedAtText && (
                                <Typography component={'span'} variant={'caption'} sx={{display: 'block', px: 1.5, color: BOX_LABEL}}>
                                    {signedAtText}
                                </Typography>
                            )}
                        />
                    </SignatureBox>
                )}
                consent={consentTextOf(snapshot, t, language)}
            />
        </SigningFrame>
    );
}

export default DigitalSigningSignedView;
